import { Icon } from "../components/Icon";
import { Avatar } from "../components/Avatar";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MessageHoldMenu } from "../components/MessageHoldMenu";
import { HOLD_MS, HOLD_SLOP } from "../lib/holdPlacement";
import { supabase } from "../lib/supabase";
import { LocalClockLine } from "../components/LocalClockLine";
import { Workspace } from "../components/Shell";
import { VoiceNote } from "../components/VoiceNote";
import { COMPOSER_MAX, ReviewComposer } from "../components/ReviewComposer";
import { reviewRemove, reviewUpload, SentMedia, useReviewRecorder } from "../components/ReviewMedia";
import { chatCaption, chatSendPlan, mediaFields, uploadItems, type Slot } from "../lib/reviewMedia";
import { forgetUploads, runUploads, undoUploads } from "../lib/mediaUpload";
import { href } from "../lib/route";
import { foldLine, splitTitled } from "../lib/reviewBody";
import { dayKey, dayKeyOf, calendarToday } from "../lib/days";
import { probeReactions, probeVoice, reactionsFrom, voiceBucket } from "./inboxSurfaces";
import {
  bodyMaxLength,
  composerState,
  dayLine,
  emptySentence,
  filterThreads,
  inboxCounts,
  inboxFilters,
  type InboxFilter,
  markableIds,
  type MessageRow,
  quoteOf,
  reactionChips,
  reactionSet,
  type ReactionRow,
  reactionWrite,
  messageRemoval,
  receiptTargetId,
  searchIndex,
  runStartIds,
  sendability,
  stampLines,
  type ThreadLink,
  type ThreadRow,
  threadPreview, listPreview,
  threadRows, receiptFor, receiptLabel, messageAge,
  threadTime,
  timeline,
  voiceNoteBody,
  voicePath,
} from "../lib/messages";

/* The Inbox — the coach's message threads, read and written on the web.
 *
 * 🔴 W72 STEP 6 — PROPOSAL 6, HIS YES 2026-09-25 ~00:45 ("yes on 1-8"), in OUR grammar (Proposal 7's rules; the
 * mocks' system font and pill buttons are NOT built). What changed, and where each old job went:
 *   · the list: `Inbox` + THE COACH'S DAY IN ONE LINE (`2 unread · 1 awaiting your reply` — `3 trained today` went 10-09,
 *     `dayLine`), one search over names AND messages, three chips each with its own empty sentence, rows 64 px
 *     (monogram · name + an accent dot when unread · the preview · the time right). ⚠️ THE RIGHT-EDGE BAR IS GONE —
 *     W26(d) copied it from theirs; Proposal 7 rule 10 names "the blue right-edge selected bar" as a signature we
 *     may not wear, so the selected row is the SURFACE fill, no bar on either edge, and no `⋯` at rest.
 *   · the thread: the head says where the client IS (`2:36 PM in Athens · Thursday · Open client ›`); the `Recent
 *     training` rail under the composer went (W72), and the `✓ Trained · … · N sets · time` line its data became went
 *     too — 🔴 HIS 2026-10-09 WORD: "there needs to be just the chat man! minimal and simple, no extra info like that!"
 *     The thread draws ONLY MESSAGES (`isMessage`, the phone's `ChatRow.isMessage` twin); who trained is read on the
 *     roster's week line, the client page's calendar and the Dashboard's queue. Bubbles carry no accent (principle 8); the time is stamped ONCE per
 *     run (the phone's five-minute rule, `stampedIds`); `Seen` sits under the coach's last message only.
 *   · the gutter on hover — ONE ⋯ (job 3 step 5 page 5, 2026-10-09: was React · Reply · ⋯, 39 buttons on 13 messages) whose
 *     menu holds the six reactions as a row, then Reply, then Copy text — and a quote line above a reply that scrolls to
 *     the original.
 *   · the reply is THE ONE COMPOSER (job 3 step 5 page 5, his 10-09 word): `ReviewComposer` — the growing field with the mic
 *     inside (click, or hold to record while held, ≤ 5 min); the take sits in the field with its player and × until Send;
 *     words or a take turn the mic into Send (W84-4). Voice notes play in a player bubble.
 *
 * ⚠️ REACTIONS AND VOICE NOTES WAIT ON HIS WORD (`LAUNCH.md` §6, the SECURITY row): the table and the bucket are
 * written and rehearsed, NOT pushed. Both are probed at load (`inboxSurfaces.ts`) and their controls are simply not
 * drawn until the probe says the surface exists — so the day he says go, nothing here is rebuilt.
 *
 * ── WHAT THIS SCREEN DELIBERATELY DOES NOT DO ───────────────────────────────────────────────
 *
 * ⚠️ IT DOES NOT POLL, AND IT HAS NO REALTIME SUBSCRIPTION — the 2026-08-28 cost stands. `docs/V8-MESSAGING-DESIGN.md`
 * §7 put both out of scope: two active links do not justify a websocket, and a poll is a request every few seconds
 * forever. Opening a thread fetches it; sending appends. A reply that arrives while the coach is looking at the
 * thread is not shown until they navigate — the honest trade at this size, first to revisit at a hundred clients.
 *
 * ⚠️ NO `Delete message`: a destructive live write, his (Proposal 6, "what is deliberately not there"). The gutter's
 * `⋯` holds `Copy text` — a real action, so it is not a control with nothing behind it (W26(a)'s finding).
 *
 * ⚠️ AND IT DOES NOT SCAN OUTGOING MESSAGES FOR RED FLAGS, WHICH IS DERIVED RATHER THAN OMITTED.
 * `ChatTransport.scansOutgoingForRedFlags` is `readerId == clientId` — a property of the SENDER'S side, because the
 * advisory exists for somebody describing their own body. Here the sender is always the coach.
 */

type State =
  | { status: "loading" }
  | { status: "error"; message: string }
  /* ⚠️ `zoneByClient` IS A SIBLING OF `statusByClient` AND NOT A FIELD ON `ThreadRow`, on purpose: a per-client fact
     only this screen reads does not belong in a type three other callers share. */
  | {
      status: "ready"; threads: ThreadRow[]; messages: MessageRow[]; statusByClient: Map<string, string>;
      zoneByClient: Map<string, string | null>;
    };

const MESSAGE_COLUMNS = "id, coach_id, client_id, sender_id, body, created_at, read_at, reply_to, audio_path, audio_seconds, kind, session_id, video_path, video_seconds, photo_path";

type MessageWire = {
  id: string; coach_id: string; client_id: string; sender_id: string; body: string; created_at: string;
  read_at: string | null; reply_to: string | null; audio_path: string | null; audio_seconds: number | null;
  kind: string; session_id: string | null;
  video_path: string | null; video_seconds: number | null; photo_path: string | null;
};

function toRow(m: MessageWire): MessageRow {
  return {
    id: m.id, coachId: m.coach_id, clientId: m.client_id, senderId: m.sender_id, body: m.body,
    createdAtISO: m.created_at, readAtISO: m.read_at, replyTo: m.reply_to, audioPath: m.audio_path,
    audioSeconds: m.audio_seconds, kind: m.kind, sessionId: m.session_id,
    videoPath: m.video_path, videoSeconds: m.video_seconds, photoPath: m.photo_path,
  };
}

function prefersReducedMotion(): boolean {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return true; }
}

export function Inbox({ coachId, selectedClientId }: { coachId: string; selectedClientId: string | null }) {
  const [state, setState] = useState<State>({ status: "loading" });
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [filter, setFilter] = useState<InboxFilter>("all");
  const [query, setQuery] = useState("");
  const [surfaces, setSurfaces] = useState<{ reactions: boolean; voice: boolean }>({ reactions: false, voice: false });
  const [reactions, setReactions] = useState<ReactionRow[]>([]);
  const [replyingTo, setReplyingTo] = useState<MessageRow | null>(null);
  /* The ONE held message (its id): his 10-15 word — no ⋯ on a bubble; a held press, a right-click or Enter on a focused bubble
     opens `MessageHoldMenu` (the six above, Reply · Copy text below, the thread blurred). */
  const [menu, setMenu] = useState<string | null>(null);
  /** The press being held: where it began (a move past `HOLD_SLOP` is a scroll or a selection) and its timer. */
  const press = useRef<{ x: number; y: number; timer: number } | null>(null);
  /** The click that ends a press which OPENED the menu must not also act on the bubble (a link, a quote, a voice play). */
  const swallowClick = useRef(false);
  /** His 10-16 word — messages gone from this reader's thread: unsent (gone for both) or deleted for me (`coach_message_hidden`). */
  const [gone, setGone] = useState<Set<string>>(() => new Set());
  const [flash, setFlash] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const threadHeading = useRef<HTMLHeadingElement>(null);
  const draftBox = useRef<HTMLTextAreaElement | null>(null);
  /* The thread's ONE recorder (the review's engine, `useReviewRecorder`): one slot, a voice note at most, a Blob in this tab
     until Send. A thread change drops an unsent take. */
  const voiceDraft = useReviewRecorder();
  const bottom = useRef<HTMLDivElement>(null);
  const conversation = useRef<HTMLElement>(null);

  /* ⚠️ ONE CLOCK FOR THE WHOLE SCREEN. `todayRef` dates every row against the
     same instant; a second `new Date()` in this file would be a second answer to one question (W21's ruler 2b). */
  const todayRef = useMemo(() => calendarToday(), []);
  const todayKey = useMemo(() => dayKeyOf(todayRef), [todayRef]);

  const load = useCallback(async () => {
    // Both reads go through RLS as the signed-in coach — the same policies the phone meets, executed per identity
    // (`tools/b4-12-messaging/00-rehearse.sql`, and for the W72 columns `tools/w72-inbox-model/rehearse.sh`).
    const [links, messages] = await Promise.all([
      supabase
        .from("coach_clients")
        .select("client_id, status, invited_first_name, invited_last_name, invited_email"),
      // No `coach_id` filter is needed for CORRECTNESS — the SELECT policy is the pair — but one is carried anyway,
      // because a query that depends on a policy for its result set reads as broader than it is.
      supabase
        .from("coach_messages")
        .select(MESSAGE_COLUMNS)
        .eq("coach_id", coachId),
    ]);

    if (links.error) return setState({ status: "error", message: links.error.message });
    if (messages.error) return setState({ status: "error", message: messages.error.message });

    const clientIds = links.data.map((l) => l.client_id).filter((id): id is string => id !== null);
    const profiles = clientIds.length > 0
      ? await supabase.from("profiles").select("id, first_name, last_name, timezone").in("id", clientIds)
      : ({ data: [], error: null } as const);
    if (profiles.error) return setState({ status: "error", message: profiles.error.message });

    const profileById = new Map((profiles.data ?? []).map((p) => [p.id, p]));
    const threadLinks: ThreadLink[] = links.data.map((l) => ({
      clientId: l.client_id,
      status: l.status,
      firstName: l.client_id === null ? null : profileById.get(l.client_id)?.first_name ?? null,
      lastName: l.client_id === null ? null : profileById.get(l.client_id)?.last_name ?? null,
      invitedFirstName: l.invited_first_name,
      invitedLastName: l.invited_last_name,
      invitedEmail: l.invited_email,
    }));

    const rows: MessageRow[] = (messages.data as MessageWire[]).map(toRow);

    const statusByClient = new Map<string, string>();
    for (const l of links.data) if (l.client_id !== null) statusByClient.set(l.client_id, l.status);

    /* W20(e) — THE CLIENT'S OWN CLOCK. A client with no profile row and a client whose profile has no zone both
       land on `null`: the reader's only question is "do we know what time it is where they are". */
    const zoneByClient = new Map<string, string | null>();
    for (const id of clientIds) zoneByClient.set(id, profileById.get(id)?.timezone ?? null);

    setState({ status: "ready", threads: threadRows(threadLinks, rows, coachId), messages: rows, statusByClient, zoneByClient });
  }, [coachId]);

  useEffect(() => {
    void load();
  }, [load]);

  /* The two surfaces that wait on his word — asked once, fail closed. */
  useEffect(() => {
    let cancelled = false;
    void Promise.all([probeReactions(), probeVoice(coachId)]).then(([r, v]) => {
      if (!cancelled) setSurfaces({ reactions: r, voice: v });
    });
    return () => { cancelled = true; };
  }, [coachId]);

  // Focus moves on arrival, because a hash navigation moves nothing on its own (`b4-7`'s ruler 10).
  useEffect(() => {
    if (selectedClientId === null) heading.current?.focus();
    else threadHeading.current?.focus();
    setReplyingTo(null);
    setMenu(null);
    voiceDraft.stopAll();
    voiceDraft.clear([THREAD_SLOT]);
    // `voiceDraft`'s calls act through refs; the effect keys on the thread alone.
  }, [selectedClientId]);

  const selected = selectedClientId;

  const threadMessages = useMemo(
    () => (state.status === "ready" && selected !== null ? state.messages.filter((m) => m.clientId === selected && !gone.has(m.id)) : []),
    [state, selected, gone],
  );

  /* His 10-16 word — what this reader deleted for themselves stays gone (any error = show everything). */
  const allThreadIdsKey = state.status === "ready" && selected !== null
    ? state.messages.filter((m) => m.clientId === selected).map((m) => m.id).join(",") : "";
  useEffect(() => {
    if (allThreadIdsKey === "") return;
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase.from("coach_message_hidden").select("message_id").in("message_id", allThreadIdsKey.split(","));
      if (cancelled || error !== null || data === null) return;
      const ids = (data as { message_id: string }[]).map((r) => r.message_id);
      if (ids.length > 0) setGone((g) => new Set([...g, ...ids]));
    })();
    return () => { cancelled = true; };
  }, [allThreadIdsKey]);

  /** Unsend: gone for both while unseen — the database decides at the moment of the call; if they read it in between, it is
   *  deleted for me instead and the thread says so (never a silent no). */
  async function unsend(m: MessageRow) {
    const { data, error } = await supabase.rpc("unsend_message", { p_message_id: m.id });
    if (error !== null) { setSendError(error.message); return; }
    if (data === true) {
      setGone((g) => new Set([...g, m.id]));
      // The photo / video object goes too (best effort — the row that named it is already gone).
      const paths = [m.photoPath, m.videoPath].filter((x): x is string => typeof x === "string");
      if (paths.length > 0) void supabase.storage.from("coach-review-media").remove(paths);
      return;
    }
    await deleteForMe(m);
    setSendError("They had already seen it — deleted for you.");
  }

  /** Delete for you: hidden for this reader only; the other side still has it. */
  async function deleteForMe(m: MessageRow) {
    const { error } = await supabase.from("coach_message_hidden").insert({ message_id: m.id });
    if (error !== null) { setSendError(error.message); return; }
    setGone((g) => new Set([...g, m.id]));
  }

  /* Reactions for the open thread, once the table exists. */
  const threadIdsKey = threadMessages.map((m) => m.id).join(",");
  useEffect(() => {
    if (!surfaces.reactions || threadIdsKey === "") { setReactions([]); return; }
    let cancelled = false;
    void (async () => {
      const { data, error } = await reactionsFrom().select("message_id, user_id, emoji").in("message_id", threadIdsKey.split(","));
      if (cancelled || error) return;
      setReactions((data ?? []).map((r: { message_id: string; user_id: string; emoji: string }) =>
        ({ messageId: r.message_id, userId: r.user_id, emoji: r.emoji })));
    })();
    return () => { cancelled = true; };
  }, [surfaces.reactions, threadIdsKey]);

  // Opening a thread IS reading it. The ids come from the rule, mirroring the UPDATE policy's own
  // `sender_id <> auth.uid()` — a request that silently matched zero rows would leave every message unread forever.
  useEffect(() => {
    if (state.status !== "ready" || selected === null) return;
    const ids = markableIds(threadMessages, coachId);
    if (ids.length === 0) return;
    let cancelled = false;
    void (async () => {
      const stamp = new Date().toISOString();
      const { error } = await supabase.from("coach_messages").update({ read_at: stamp }).in("id", ids);
      if (error) return;
      // W81 — the sidebar's unread badge re-reads its count now, rather than at the next navigation.
      window.dispatchEvent(new Event("ht:inbox-read"));
      if (cancelled) return;
      setState((prev) => {
        if (prev.status !== "ready") return prev;
        const set = new Set(ids);
        return {
          ...prev,
          messages: prev.messages.map((m) => (set.has(m.id) ? { ...m, readAtISO: stamp } : m)),
          threads: prev.threads.map((t) => (t.clientId === selected ? { ...t, unread: 0 } : t)),
        };
      });
    })();
    return () => { cancelled = true; };
    // `threadMessages` is derived, so the effect keys on what actually changes.
  }, [state.status, selected, coachId, threadMessages.length]);

  // Newly arrived or newly sent messages belong in view.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [threadMessages.length, selected]);

  /* His 10-10 ask (2): a photo takes its height only once its signed file arrives — AFTER the jump above — so a picture that
     loads while the reader is at the foot keeps the foot in view (the phone's `.defaultScrollAnchor(.bottom, for:
     .sizeChanges)`; RENDERED: without it the thread opened with the last message under the composer). A reader scrolled up
     into the history is left where they are. `load` does not bubble — it is caught on the way down. */
  useEffect(() => {
    const el = conversation.current;
    if (el === null) return;
    let atFoot = true;
    const onScroll = () => { atFoot = el.scrollHeight - el.scrollTop - el.clientHeight < 96; };
    const onLoad = (e: Event) => { if (atFoot && e.target instanceof HTMLImageElement) bottom.current?.scrollIntoView({ block: "end" }); };
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("load", onLoad, true);
    return () => { el.removeEventListener("scroll", onScroll); el.removeEventListener("load", onLoad, true); };
  }, [selected, state.status, threadMessages.length > 0]);


  /* 🔴 THE ONE SEND PATH. Text (Enter or the button) and a voice note both arrive here, so the single INSERT below is
     the only place a message is written — `inboxComposer.test.ts` counts it. A voice note uploads its object FIRST
     (the path names the message id, so the id is minted here), then writes the row; an upload that succeeds before
     an insert that fails leaves an object no row names, which the (staged) read policy keeps unreadable to everyone
     — stated, and owed to the delete-account sweep once the bucket is live. */
  async function sendMessage(p: { body: string; audio?: { blob: Blob; seconds: number }; media?: Slot }): Promise<boolean> {
    if (selected === null || sending) return false;
    setSending(true);
    setSendError(null);
    const id = crypto.randomUUID();
    let audioPath: string | null = null;
    if (p.audio !== undefined) {
      audioPath = voicePath(coachId, id);
      const up = await supabase.storage.from(voiceBucket).upload(audioPath, p.audio.blob, { contentType: "audio/mp4", upsert: false });
      if (up.error) {
        setSending(false);
        setSendError(`The voice note did not upload: ${up.error.message}`);
        return false;
      }
    }
    /* His 10-10 ask (2): a held photo / round video goes to `coach-review-media` under THIS row's id — the review's one
       queue (`runUploads`: in order, the first failure removes what landed), then the row; a refused row removes them
       again (`undoUploads`), so no object outlives a row that never was. Rehearsed per identity: `tools/chat-media/`. */
    let landed: string[] = [];
    if (p.media !== undefined) {
      const up = await runUploads(uploadItems(coachId, [{ slot: THREAD_SLOT, id }], { [THREAD_SLOT]: p.media }),
                                  (ref) => voiceDraft.blobOf(ref), reviewUpload, reviewRemove);
      if (up.error !== null) {
        setSending(false);
        setSendError(`Not sent: ${up.error}`);
        return false;
      }
      landed = up.landed;
    }
    const { data, error } = await supabase
      .from("coach_messages")
      .insert({
        id, coach_id: coachId, client_id: selected, sender_id: coachId, body: p.body,
        reply_to: replyingTo?.id ?? null,
        audio_path: audioPath, audio_seconds: p.audio?.seconds ?? null,
        ...(p.media === undefined ? {} : mediaFields(coachId, id, p.media)),
      })
      .select(MESSAGE_COLUMNS)
      .single();
    if (error || data === null) await undoUploads(landed, reviewRemove);
    else forgetUploads(landed);
    setSending(false);
    if (error || data === null) {
      // The server's own words. The commonest refusal is the INSERT policy's ACTIVE requirement (D-V8.2), which
      // `composerState` normally prevents reaching — but the database is the enforcement.
      setSendError(error?.message ?? "The message did not come back from the server.");
      return false;
    }
    const row = toRow(data as MessageWire);
    setReplyingTo(null);
    setState((prev) =>
      prev.status !== "ready"
        ? prev
        : {
            ...prev,
            messages: [...prev.messages, row],
            /* through the fold, like the list's first read: a reply under a review's comment is that review's `· 1 reply` */
            threads: prev.threads.map((t) =>
              t.clientId === selected
                ? { ...t, preview: listPreview([...prev.messages.filter((m) => m.clientId === selected), row]), lastAtISO: row.createdAtISO, awaiting: false }
                : t),
          },
    );

    /* ⚠️ FIRE AND FORGET, AND EVERY FAILURE IS SWALLOWED. The message is already stored; a notification error
       surfaced here would read as the message not having sent. The endpoint decides for itself whether this caller
       may notify (it re-reads the row under the coach's own JWT, then narrows to `sender_id = caller`). */
    void supabase.functions
      .invoke("send-message-push", { body: { messageId: row.id } })
      .catch(() => undefined);
    return true;
  }

  /* ⚠️ THE EVENT IS OPTIONAL AND THE HANDLER IS ONE. His 2026-09-20 ask (Enter sends) is answered by a SECOND CALLER,
     never a second send. */
  async function onSend(event?: React.SyntheticEvent) {
    event?.preventDefault();
    if (selected === null || sending) return;
    const judged = sendability(draft);
    /* ONE Send, in `chatSendPlan`'s order (the phone's `ChatSendPlan`): a held voice note first, as its own row; then a held
       photo / round video as ONE row with the words as its caption (his 10-10 ask 2), or else the words alone. */
    const slot = voiceDraft.rec.slots[THREAD_SLOT];
    const plan = chatSendPlan(draft, slot);
    if (judged.kind === "tooLong" || plan.length === 0) {
      setSendError(
        judged.kind === "tooLong"
          ? `That is ${judged.length} characters; the limit is ${judged.max}.`
          : "Write something first.",
      );
      return;
    }
    for (const step of plan) {
      if (step.kind === "voice") {
        const take = slot?.voice;
        const blob = take === undefined ? undefined : voiceDraft.blobOf(take.ref);
        if (take === undefined || blob === undefined) continue;
        if (!(await sendMessage({ body: voiceNoteBody, audio: { blob, seconds: take.seconds } }))) return;
        voiceDraft.remove(THREAD_SLOT, "voice");
      } else if (step.kind === "media") {
        const media: Slot = { ...(slot?.video === undefined ? {} : { video: slot.video }), ...(slot?.photo === undefined ? {} : { photo: slot.photo }) };
        if (!(await sendMessage({ body: step.body, media }))) return;
        voiceDraft.clear([THREAD_SLOT]);
        setDraft("");
      } else if (await sendMessage({ body: step.body })) setDraft("");
    }
  }

  async function react(messageId: string, emoji: string) {
    const mine = reactions.find((r) => r.messageId === messageId && r.userId === coachId)?.emoji ?? null;
    const w = reactionWrite(mine, emoji);
    setMenu(null);
    // One per person, and no UPDATE grant: a change is a remove then an add.
    if (w.kind !== "add") {
      const del = await reactionsFrom().delete().eq("message_id", messageId).eq("user_id", coachId);
      if (del.error) return;
    }
    if (w.kind !== "remove") {
      const ins = await reactionsFrom().insert({ message_id: messageId, user_id: coachId, emoji: w.emoji });
      if (ins.error) return;
    }
    setReactions((prev) => [
      ...prev.filter((r) => !(r.messageId === messageId && r.userId === coachId)),
      ...(w.kind === "remove" ? [] : [{ messageId, userId: coachId, emoji: w.emoji }]),
    ]);
  }

  /** A bubble's hold: a press kept `HOLD_MS` without travelling, a right-click, or Enter / Space / the menu key / ⇧F10 on the
   *  focused bubble itself (never on a control inside it) opens the menu. */
  function holdProps(id: string) {
    const cancel = () => { if (press.current !== null) { window.clearTimeout(press.current.timer); press.current = null; } };
    return {
      onPointerDown: (e: React.PointerEvent) => {
        if (e.button !== 0) return;
        cancel();
        const timer = window.setTimeout(() => { press.current = null; swallowClick.current = true; setMenu(id); }, HOLD_MS);
        press.current = { x: e.clientX, y: e.clientY, timer };
      },
      onPointerMove: (e: React.PointerEvent) => {
        const p = press.current;
        if (p !== null && Math.hypot(e.clientX - p.x, e.clientY - p.y) > HOLD_SLOP) cancel();
      },
      onPointerUp: cancel,
      onPointerCancel: cancel,
      onPointerLeave: cancel,
      onContextMenu: (e: React.MouseEvent) => { e.preventDefault(); cancel(); setMenu(id); },
      onClickCapture: (e: React.MouseEvent) => {
        if (!swallowClick.current) return;
        swallowClick.current = false;
        e.preventDefault();
        e.stopPropagation();
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " " || e.key === "ContextMenu" || (e.key === "F10" && e.shiftKey)) {
          e.preventDefault();
          setMenu(id);
        }
      },
    };
  }

  function jumpTo(id: string) {
    const el = document.getElementById(`msg-${id}`);
    if (el === null) return;
    el.scrollIntoView({ block: "center", behavior: prefersReducedMotion() ? "auto" : "smooth" });
    el.focus({ preventScroll: true });
    setFlash(id);
    window.setTimeout(() => setFlash((f) => (f === id ? null : f)), 1400);
  }

  function startReply(m: MessageRow) {
    setReplyingTo(m);
    setMenu(null);
    draftBox.current?.focus();
  }

  const threads = state.status === "ready" ? state.threads : [];
  const index = useMemo(() => searchIndex(state.status === "ready" ? state.messages : []), [state]);
  const shown = filterThreads(threads, filter, query, index);
  const counts = inboxCounts(threads);
  const day = dayLine({
    unread: state.status === "ready" ? counts.unread : null,
    awaiting: state.status === "ready" ? counts.awaiting : null,
  });
  const selectedThread = threads.find((t) => t.clientId === selected) ?? null;
  const composer = composerState(
    selected === null ? null : state.status === "ready" ? state.statusByClient.get(selected) ?? null : null,
  );
  // His 10-15 word: the stamp carries the day only when it changes (`stampLines`); a sender's run sits tight (`runStartIds`).
  const stamps = stampLines(threadMessages, todayRef, clockTime);
  const runStarts = runStartIds(threadMessages, stamps);
  const receiptId = receiptTargetId(threadMessages, coachId);

  /* Column 2 — the threads. Real links, because choosing one IS navigation: it changes the address, so a
     conversation can be linked to, bookmarked and reopened. The chips filter this one loaded list and change no
     address, so they are toggle buttons. */
  const list = (
    <div className="inbox-listcol">
      <header className="inbox-listhead">
        {selected === null ? (
          /* 🔴 W21 (D) — ONE `<h1>`, two mutually exclusive spans: `Inbox` wide, the app's `Messages` on a phone.
             `heading` is the focus ref this screen moves to on every navigation into it. */
          <h1 className="ht-title ours-title" tabIndex={-1} ref={heading}>
            <span className="inbox-title-wide">Inbox</span>
            <span className="inbox-title-phone">Messages</span>
          </h1>
        ) : (
          <p className="ht-title ours-title inbox-listtitle" aria-hidden="true">
            <span className="inbox-title-wide">Inbox</span>
            <span className="inbox-title-phone">Messages</span>
          </p>
        )}
        {/* JOB 3 · WILLIAM (10-15, "Chat box could be cleaner"): the day line (`1 unread · 2 awaiting your reply`) said the
            chips again — each chip carries its count of CONVERSATIONS now (what it filters to) (the Library's and Clients' `Active 4` shape); the line
            stays for the screen reader only, still live. */}
        {day.length > 0 && (
          <p className="ours-dayline inbox-dayline ht-visually-hidden" aria-live="polite">
            {day.map((p, i) => (
              <span key={p.kind}>
                {i > 0 && <span aria-hidden="true"> · </span>}
                {p.kind === "unread" && p.count > 0 ? <strong className="is-needed">{p.text}</strong> : p.text}
              </span>
            ))}
          </p>
        )}
        <label className="ours-search inbox-search">
          <span className="ht-visually-hidden">Search messages</span>
          <input className="ht-input" type="search" placeholder="Search messages…" value={query}
                 onChange={(e) => setQuery(e.target.value)} />
        </label>
        <div className="ours-chips" role="group" aria-label="Show">
          {inboxFilters.map((f) => (
            <button key={f.id} type="button" className="ours-chip" aria-pressed={filter === f.id}
                    onClick={() => setFilter(f.id)}>
              {f.label}
              {f.id !== "all" && state.status === "ready" && (
                <> <span className="ours-chip-count">{filterThreads(threads, f.id, "", index).length}</span></>
              )}
            </button>
          ))}
        </div>
      </header>
      {state.status === "ready" && (
        <ul className="inbox-threads">
          {shown.map((t) => (
            <li key={t.clientId}>
              <a
                className={t.unread > 0 ? "inbox-thread is-unread" : "inbox-thread"}
                href={href({ name: "thread", clientId: t.clientId })}
                aria-current={t.clientId === selected ? "page" : undefined}
              >
                <Avatar name={t.name} className="ours-disc inbox-disc" />
                <span className="inbox-thread-text">
                  <span className="inbox-thread-top">
                    {t.unread > 0 && <span className="inbox-dot" aria-hidden="true" />}
                    <span className="inbox-thread-name">{t.name}</span>
                    {/* The count is ANNOUNCED; the dot carries it visually — never the state in colour alone. */}
                    {t.unread > 0 && <span className="ht-visually-hidden">, {t.unread} unread</span>}
                    {t.awaiting && <span className="ht-visually-hidden">, awaiting your reply</span>}
                  </span>
                  <span className="inbox-thread-preview">
                    {t.preview !== "" ? t.preview : t.isFormerClient ? "No longer on your roster" : "No messages yet"}
                  </span>
                </span>
                {/* Desktop: `14:02` / `Tue` / `12 Sep` (Proposal 6). Phone: W21 (D)'s `messageAge`, the app's row. */}
                <span className="inbox-thread-time">{threadTime(t.lastAtISO, todayKey, dayKey, clockTime)}</span>
                <span className="ht-caption inbox-thread-age">{messageAge(t.lastAtISO, todayRef)}</span>
              </a>
            </li>
          ))}
          {shown.length === 0 && (
            <li className="inbox-thread-empty">{emptySentence(filter, query, threads.length)}</li>
          )}
        </ul>
      )}
    </div>
  );

  return (
    <Workspace list={list} listLabel="Conversations">
      <main id="main" className="inbox-page">
        {selected !== null && (
          <header className="inbox-head">
            <h1 className="ht-title inbox-thread-title" tabIndex={-1} ref={threadHeading}>
              {selectedThread?.name ?? "Conversation"}
            </h1>
            {/* W20(e) + Proposal 6 — where the client IS: their time, their city, their weekday, then the page. */}
            <p className="inbox-where">
              <LocalClockLine weekday zone={state.status === "ready" ? state.zoneByClient.get(selected) ?? null : null}
                              className="inbox-local-clock" />
              {selectedThread !== null && !selectedThread.isFormerClient && (
                <a className="inbox-open-client" href={href({ name: "client", clientId: selected })}>Open client ›</a>
              )}
            </p>
          </header>
        )}

        {state.status === "loading" && (
          <p className="ht-callout loading-line" aria-live="polite">Loading…</p>
        )}

        {state.status === "error" && (
          <div className="inbox-error" role="alert">
            <p className="ht-body">Could not load your messages.</p>
            <p className="ht-callout">{state.message}</p>
          </div>
        )}

        {state.status === "ready" && selected === null && (
          <p className="ht-body inbox-body">
            Pick a conversation to read it. Every client on your roster has one, whether or not anybody has written
            yet — and what you send here arrives on their phone.
          </p>
        )}

        {state.status === "ready" && selected !== null && selectedThread === null && (
          <p className="ht-callout inbox-body">
            This is not one of your conversations. If you have just invited them, their thread appears once they accept.
          </p>
        )}

        {state.status === "ready" && selectedThread !== null && (
          <>
            <section className="inbox-conversation" aria-label="Conversation" ref={conversation}>
              {threadMessages.length === 0 && (
                <p className="ht-callout inbox-empty">Nothing has been said yet. Write the first message.</p>
              )}
              {timeline(threadMessages, coachId, dayKey).map((block, i) => (
                <div className="inbox-day" key={block.dayKey ?? `undated-${i}`}>
                  {/* The centred day heading went 2026-10-15 (his word; iMessage's shape): the day rides the stamp under the
                      message where it changes — `stampLines`. The block still groups a day's rows. */}
                  {block.items.map((item) => {
                    if (item.kind === "review") {
                      // THE FOLD (W82 step 4): the review and its comments are ONE bubble; the line opens the review page
                      // (the coach's side of the finished workout), or says nothing more once the workout was reset.
                      const words = splitTitled(item.review.body).text;
                      const line = foldLine(item.review, item.comments.length, item.replies.length);
                      return (
                        <div key={item.review.id} className={item.mine ? "inbox-msg-row is-mine" : "inbox-msg-row"}>
                          <div className="inbox-msg-line">
                            <article id={`msg-${item.review.id}`} tabIndex={-1} className={`inbox-msg inbox-review${item.mine ? " inbox-msg-mine" : ""}`}>
                              <span className="ht-visually-hidden">{item.mine ? "You: " : `${selectedThread.name}: `}</span>
                              {/* JOB 3 · WILLIAM (10-15, "Chat box could be cleaner"): a review card is told from a message by a
                                  WORD and a glyph, never by bold alone — `Review ›` (the link to the review page) over its line in
                                  the body's weight; three bold one-liners in a row read as shouting and looked alike. */}
                              {item.review.sessionId != null
                                ? <a className="inbox-review-kicker" href={href({ name: "review", sessionId: item.review.sessionId })}>
                                    <Icon name="dumbbell" size={13} stroke={2} /> Review ›</a>
                                : <p className="inbox-review-kicker"><Icon name="dumbbell" size={13} stroke={2} /> Review</p>}
                              <p className="inbox-review-line">{line}</p>
                              {words !== "" && <p className="inbox-msg-body">{words}</p>}
                            </article>
                          </div>
                          {/* His 10-15 word: with the day headings gone, a review card carries its day and time like any message. */}
                          {stamps.has(item.review.id) && <p className="inbox-msg-meta">{stamps.get(item.review.id)}</p>}
                        </div>
                      );
                    }
                    const { message, mine } = item;
                    const quote = quoteOf(message, threadMessages);
                    const chips = surfaces.reactions ? reactionChips(reactions, message.id, coachId) : [];
                    const receipt = message.id === receiptId ? receiptFor(message, coachId) : null;
                    const isVoice = typeof message.audioPath === "string" && typeof message.audioSeconds === "number";
                    const hasMedia = typeof message.photoPath === "string" || (typeof message.videoPath === "string" && typeof message.videoSeconds === "number");
                    const caption = hasMedia ? chatCaption(message.body) : null;
                    return (
                      <div key={message.id} className={`${mine ? "inbox-msg-row is-mine" : "inbox-msg-row"}${runStarts.has(message.id) ? "" : " is-run"}`}>
                        <div className="inbox-msg-line">
                          <article id={`msg-${message.id}`} title={clockTime(message.createdAtISO)}
                                   className={`inbox-msg${mine ? " inbox-msg-mine" : ""}${flash === message.id ? " is-flash" : ""}`}
                                   {...(composer.kind === "open"
                                     ? { tabIndex: 0, "aria-haspopup": "menu" as const, "aria-keyshortcuts": "Enter", ...holdProps(message.id) }
                                     : { tabIndex: -1 })}>
                            <span className="ht-visually-hidden">{mine ? "You: " : `${selectedThread.name}: `}</span>
                            {quote !== null && (
                              <button type="button" className="inbox-quote" onClick={() => jumpTo(quote.id)}
                                      disabled={!quote.found}
                                      aria-label={`Replying to: ${quote.text}. Show the original`}>
                                <span aria-hidden="true">↩ </span>{quote.text}
                              </button>
                            )}
                            {isVoice
                              ? <VoiceNote path={message.audioPath as string} seconds={message.audioSeconds as number} enabled={surfaces.voice} />
                              : hasMedia
                                /* His 10-10 ask (2): the photo / round video (the review's `SentMedia` — signed for this reader, the
                                   sender's × removes the object, a removed one says so), then its caption. */
                                ? <div className="inbox-msg-media">
                                    <SentMedia row={{ id: message.id, senderId: message.senderId, audioPath: null, audioSeconds: null,
                                                      videoPath: message.videoPath ?? null, videoSeconds: message.videoSeconds ?? null,
                                                      photoPath: message.photoPath ?? null }}
                                               home="inbox" viewerId={coachId} senderIsCoach={mine} stage />
                                    {caption !== null && <p className="inbox-msg-body">{caption}</p>}
                                  </div>
                                : <p className="inbox-msg-body">{message.body}</p>}
                            {chips.length > 0 && (
                              <span className="inbox-reactions">
                                {chips.map((c) => (
                                  <button key={c.emoji} type="button" className={c.mine ? "inbox-reaction is-mine" : "inbox-reaction"}
                                          onClick={() => void react(message.id, c.emoji)}
                                          aria-pressed={c.mine}
                                          aria-label={`${c.emoji} ${String(c.count)}${c.mine ? ", yours — tap to remove" : ""}`}>
                                    {c.emoji}{c.count > 1 ? <span className="inbox-reaction-n">{c.count}</span> : null}
                                  </button>
                                ))}
                              </span>
                            )}
                          </article>
                        </div>
                        {/* HIS WORD 10-15 (job 3 · William): the stamp only where the DAY changes (`stampLines`); every other
                            message's time is its bubble's tooltip (`title` above). The receipt still rides its own line. */}
                        {(stamps.has(message.id) || receipt !== null) && (
                          <p className="inbox-msg-meta">
                            {stamps.get(message.id) ?? ""}
                            {/* 🔴 THE READ RECEIPT — ONCE, under the coach's last message (`receiptTargetId`). */}
                            {receipt !== null && (
                              <span className={receipt === "seen" ? "inbox-receipt is-seen" : "inbox-receipt"}>
                                {stamps.has(message.id) && <span aria-hidden="true"> · </span>}{receiptLabel(receipt)}
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
              <div ref={bottom} />
            </section>

            {composer.kind === "open" ? (
              <form className="inbox-reply" onSubmit={onSend}>
                {replyingTo !== null && (
                  <p className="inbox-replying">
                    <span className="inbox-replying-text">
                      <span aria-hidden="true">↩ </span>Replying to: {threadPreview(replyingTo, 80)}
                    </span>
                    <button type="button" className="ours-text-action" onClick={() => setReplyingTo(null)}>Cancel reply</button>
                  </p>
                )}
                {/* 🔴 THE ONE COMPOSER — the review's, by props (no +; the mic behind its probe; the W84-4 mic/Send switch).
                    ⚠️ A BOX THAT SENDS ON ENTER MUST SAY SO: the one-line capsule is the chat shape (his iMessage word),
                    where Enter sends; the words stay — the field's description and Send's tooltip — the visible caption
                    under the old three-row box went. */}
                <ReviewComposer slot={THREAD_SLOT} r={voiceDraft} locked={false} sendingPath={() => null}
                                name={selectedThread.name} id="inbox-draft" label={`Message ${selectedThread.name}`}
                                placeholder="Message" text={draft} maxHeight={COMPOSER_MAX} maxLength={bodyMaxLength}
                                plus mic={surfaces.voice} send={{ busy: sending }} inputRef={draftBox}
                                hint="Enter to send · Shift+Enter for a new line"
                                onText={(t) => {
                                  setDraft(t);
                                  if (sendError !== null) setSendError(null);
                                }}
                                onKeyDown={(e) => {
                                  /* ⚠️ `isComposing` IS NOT OPTIONAL: an IME confirms a candidate with Enter, and without this
                                     guard that keystroke sends a half-written message. */
                                  if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
                                  e.preventDefault();
                                  void onSend(e);
                                }} />
                {(sendError ?? voiceDraft.notice ?? voiceDraft.voice.error) !== null && (
                  <p className="ht-caption inbox-send-error" role="alert">{sendError ?? voiceDraft.notice ?? voiceDraft.voice.error}</p>
                )}
              </form>
            ) : (
              /* ⚠️ THE REASON IS NAMED RATHER THAN THE BOX SIMPLY BEING ABSENT. */
              <p className="ht-callout inbox-closed">{composer.reason}</p>
            )}
          </>
        )}
        {menu !== null && selectedThread !== null && (() => {
          const held = threadMessages.find((m) => m.id === menu);
          if (held === undefined) return null;
          const voice = typeof held.audioPath === "string" && typeof held.audioSeconds === "number";
          const words = voice ? null : held.body;
          return (
            <MessageHoldMenu key={held.id} anchorId={`msg-${held.id}`} mine={held.senderId === coachId}
                             picks={surfaces.reactions ? reactionSet : null}
                             chosen={reactions.find((r) => r.messageId === held.id && r.userId === coachId)?.emoji ?? null}
                             onReact={(e) => void react(held.id, e)} onReply={() => startReply(held)}
                             copyText={words} removal={messageRemoval(held, coachId)}
                             onUnsend={() => void unsend(held)} onDeleteForMe={() => void deleteForMe(held)}
                             onClose={() => setMenu(null)} />
          );
        })()}
      </main>
    </Workspace>
  );
}


/** The thread composer's one recording slot (`useReviewRecorder` keys takes by slot). */
const THREAD_SLOT = "thread";

/** The clock time a message was sent, in the READER's own zone — which is right for a clock. */
function clockTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
